import Form, { baseError } from 'components/form'
import Input from 'components/form/Input'
import { api } from 'roadie-ui'
import { Row } from 'components/layout/flex'
import { required, minLength, exactLength, integer, runValidations } from 'components/form/validations'
import { LocalizedText, withLocalization } from 'localization'

const states =
  'AK,AL,AR,AZ,CA,CO,CT,DC,DE,FL,GA,HI,IA,ID,IL,IN,KS,KY,LA,MA,MD,ME,MI,MN,MO,MS,MT,NC,ND,NE,NH,NJ,NM,NV,NY,OH,OK,OR,PA,RI,SC,SD,TN,TX,UT,VA,VT,WA,WI,WV,WY'.split(
    ','
  )

// Mapping from full state names to abbreviations
const stateNameToAbbrev = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
}

const ValidateAddress = ({ onValidAddress, onFailure, localization }) => {
  const { t } = localization

  const handleData = (data) => {
    return api
      .post('address_validations', data)
      .then((resp) => {
        const { state: stateName, city, street, zip } = resp.data.validated_address
        // Validate that zip code is exactly 5 digits
        const zipStr = String(zip ?? '')
        const zipValidation = integer
          .and(exactLength(5))
          .withMessage(t('dweb_validation_must_be_exactly_5_digits'))
          .required()
        const zipError = runValidations(zipValidation)(zipStr)
        if (zipError) {
          return baseError(zipError)
        }
        // Convert full state name to abbreviation because the API returns the full state name
        const stateAbbrev = stateNameToAbbrev[stateName] || stateName
        const stateAndZip = [stateAbbrev, zip].filter((x) => x).join(' ')
        const addressStr = [street, city, stateAndZip].filter((x) => x).join(', ')
        onValidAddress({
          ...resp.data.validated_address,
          state: stateAbbrev,
          apt_number: data.apt_number,
          text: addressStr,
        })
      })
      .catch(() => {
        onFailure?.()
        return baseError(t('dweb_validation_address_not_valid_try_again_or_contact_support'))
      })
  }

  return (
    <Form onData={handleData}>
      <Input tight label={t('dweb_form_validate_address_street')} name="street" validate={required} />
      <Input label={t('dweb_form_validate_address_apt_building_suite_optional')} name="apt_number" maxLength={25} />
      <Input label={t('dweb_form_validate_address_city')} name="city" validate={required} />
      <Row>
        <Input.Select label={t('dweb_account_driving_info_vehicle_info_state')} name="state" validate={required}>
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </Input.Select>
        <Input
          label={t('dweb_form_validate_address_zip')}
          name="zip"
          type="tel"
          kind="zip"
          maxLength={5}
          validate={minLength(5).withMessage(t('dweb_validation_must_be_exactly_5_digits')).required()}
        />
      </Row>
      <Input.Submit>
        <LocalizedText translationKey="dweb_form_validate_address" />
      </Input.Submit>
    </Form>
  )
}

export default withLocalization(ValidateAddress)
